/**
 * Owner Data Analytics Report — business intelligence and actionable insights.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LoginHeaderOperational from "../LoginHeaderOperational";
import Sidebar_Progress from "../../layouts/Operational_Sidebar_Progress.component";
import Loader from "../Employee/Loader.component";
import { API_URL } from "../../../constant/API_Settings";
import { clearAuthStorage, getValidAuthToken } from "../../../utils/authToken";
import {
  fetchAdminPumpAnalytics,
  fetchAdminPumpAnalyticsDateDefaults,
} from "../Admin/adminApi";
import OwnerWorkingCapitalPanel from "./OwnerWorkingCapitalPanel";
import {
  formatInrAmount,
  formatIndianNumber,
  formatIndianPercent,
  formatCompactInrAxis,
} from "../../../utils/reportFormat";
import { getCreditAgeVisual } from "../../../utils/workingCapitalAgeColors";

const formatCurrency = formatInrAmount;
const formatPercent = (value) => formatIndianPercent(value, 2);
const formatNumber = (value, digits = 2) => formatIndianNumber(value, digits);

const formatScorecardValue = (row) => {
  if (!row) {
    return "—";
  }
  if (row.format === "percent") {
    return formatPercent(row.value);
  }
  return formatCurrency(row.value);
};

const severityClass = (severity) => {
  const map = {
    critical: "owner-analytics-insight--critical",
    warning: "owner-analytics-insight--warning",
    opportunity: "owner-analytics-insight--opportunity",
    positive: "owner-analytics-insight--positive",
  };
  return map[severity] || "";
};

const priorityClass = (priority) => {
  const map = {
    critical: "owner-analytics-rec--high",
    high: "owner-analytics-rec--high",
    medium: "owner-analytics-rec--medium",
    low: "owner-analytics-rec--low",
    positive: "owner-analytics-rec--positive",
  };
  return map[priority] || "";
};

const formatAxisCurrency = formatCompactInrAxis;

const ChartWrap = ({ height = 260, children }) => (
  <div className="owner-analytics-chart-wrap" style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

const focusClass = (focus) => {
  const map = {
    star: "owner-analytics-focus--star",
    maintain: "owner-analytics-focus--maintain",
    review: "owner-analytics-focus--review",
  };
  return map[focus] || "";
};

const healthScoreColor = (score) => {
  if (score >= 80) {
    return "#16A34A";
  }
  if (score >= 60) {
    return "#D97706";
  }
  if (score >= 40) {
    return "#EA580C";
  }
  return "#DC2626";
};

const OwnerAnalyticsReport = ({ embedded = false, pumpId = null, defaultFromDate = '', defaultToDate = '', syncDateRange = null, dateRangeKey = 0, hideDateFilter = false, onReportLoaded = null, ...props }) => {
  const history = useHistory();
  const authFailureHandledRef = useRef(false);
  const [loading, setLoading] = useState(!embedded);
  const [reportLoading, setReportLoading] = useState(false);
  const [access, setAccess] = useState({ allowed: false, message: "" });
  const [minDate, setMinDate] = useState("");
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState({ from_date: "", to_date: "" });
  const [report, setReport] = useState(null);

  const redirectToLogin = useCallback(() => {
    if (authFailureHandledRef.current) {
      return;
    }
    authFailureHandledRef.current = true;
    clearAuthStorage();
    history.push("/login");
  }, [history]);

  const fetchDateDefaults = useCallback(async () => {
    if (embedded && pumpId) {
      try {
        return await fetchAdminPumpAnalyticsDateDefaults(pumpId);
      } catch {
        return null;
      }
    }

    const token = getValidAuthToken();
    if (!token) {
      redirectToLogin();
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/owner-analytics/date-defaults`, {
        method: "get",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.status === 401) {
        redirectToLogin();
        return null;
      }

      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch (error) {
      return null;
    }
  }, [embedded, pumpId, redirectToLogin]);

  const fetchReport = useCallback(
    async (dateRange) => {
      if (!dateRange?.from_date || !dateRange?.to_date) {
        return;
      }

      setReportLoading(true);

      try {
        if (embedded && pumpId) {
          const data = await fetchAdminPumpAnalytics(pumpId, dateRange.from_date, dateRange.to_date);
          const nextReport = data?.status === false ? null : data;
          setReport(nextReport);
          if (onReportLoaded) {
            onReportLoaded(nextReport?.executive_summary ? {
              loaded: true,
              overall_health_score: nextReport.executive_summary.overall_health_score ?? 0,
              health_label: nextReport.executive_summary.health_label ?? 'Unknown',
              verdict: nextReport.executive_summary.verdict ?? '',
              from_date: nextReport.from_date ?? dateRange.from_date,
              to_date: nextReport.to_date ?? dateRange.to_date,
              radar: Array.isArray(nextReport.radar) ? nextReport.radar : [],
            } : {
              loaded: false,
              overall_health_score: null,
              health_label: null,
              verdict: null,
              from_date: null,
              to_date: null,
              radar: [],
            });
          }
          return;
        }

        const token = getValidAuthToken();
        if (!token) {
          redirectToLogin();
          return;
        }

        const response = await fetch(`${API_URL}/owner-analytics/report`, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          }),
        });

        if (response.status === 401) {
          redirectToLogin();
          return;
        }

        if (response.status === 403) {
          setAccess({
            allowed: false,
            message: "This report is only available to pump owners.",
          });
          setReport(null);
          return;
        }

        if (!response.ok) {
          setReport(null);
          return;
        }

        const data = await response.json();
        setReport(data);
      } catch (error) {
        setReport(null);
      } finally {
        setReportLoading(false);
      }
    },
    [embedded, pumpId, redirectToLogin, onReportLoaded]
  );

  const handleApplyDateRange = () => {
    if (!draftFromDate || !draftToDate || draftFromDate > draftToDate) {
      return;
    }

    const nextRange = { from_date: draftFromDate, to_date: draftToDate };
    setAppliedDateRange(nextRange);
    fetchReport(nextRange);
  };

  useEffect(() => {
    if (embedded && pumpId) {
      return undefined;
    }

    let cancelled = false;

    const verifyAccess = async () => {
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("AUTH_TOKEN") || ""}`,
          Accept: "application/json",
        };

        const [urlResponse, ownerResponse] = await Promise.all([
          fetch(`${API_URL}/check_url`, {
            method: "post",
            headers,
            body: JSON.stringify({ pathurl: "/owner-analytics" }),
          }),
          fetch(`${API_URL}/is_owner`, { method: "get", headers }),
        ]);

        if (urlResponse.status === 401 || ownerResponse.status === 401) {
          redirectToLogin();
          return;
        }

        const urlData = await urlResponse.json();
        const roleName = await ownerResponse.json();
        const isOwner = String(roleName || "").toLowerCase() === "owner";

        if (cancelled) {
          return;
        }

        if (!isOwner) {
          setAccess({
            allowed: false,
            message: "This report is only available to pump owners.",
          });
          setLoading(false);
          return;
        }

        if (urlData.allowurl !== 1) {
          setAccess({
            allowed: false,
            message: "You do not have permission to view this report.",
          });
          setLoading(false);
          return;
        }

        const defaults = await fetchDateDefaults();
        if (cancelled) {
          return;
        }

        if (defaults?.from_date && defaults?.to_date) {
          setMinDate(defaults.min_date || defaults.from_date);
          setDraftFromDate(defaults.from_date);
          setDraftToDate(defaults.to_date);
          const nextRange = {
            from_date: defaults.from_date,
            to_date: defaults.to_date,
          };
          setAppliedDateRange(nextRange);
          fetchReport(nextRange);
        }

        setAccess({ allowed: true, message: "" });
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          setAccess({
            allowed: false,
            message: "Unable to verify owner access. Please try again.",
          });
          setLoading(false);
        }
      }
    };

    verifyAccess();

    return () => {
      cancelled = true;
    };
  }, [embedded, pumpId, redirectToLogin, fetchDateDefaults, fetchReport]);

  useEffect(() => {
    if (!embedded || !pumpId) {
      return;
    }

    const from = syncDateRange?.from_date || defaultFromDate;
    const to = syncDateRange?.to_date || defaultToDate;
    if (!from || !to) {
      return;
    }

    const nextRange = { from_date: from, to_date: to };
    setMinDate(defaultFromDate || from);
    setDraftFromDate(from);
    setDraftToDate(to);
    setAppliedDateRange(nextRange);
    setAccess({ allowed: true, message: '' });
    setLoading(false);
    setReport(null);
    fetchReport(nextRange);
  }, [
    embedded,
    pumpId,
    dateRangeKey,
    syncDateRange?.from_date,
    syncDateRange?.to_date,
    defaultFromDate,
    defaultToDate,
    fetchReport,
  ]);

  const comparisonChartData = report?.comparison
    ? [
        {
          metric: "Sales",
          shortLabel: "Sales",
          current: report.comparison.current?.sales || 0,
          prior: report.comparison.prior?.sales || 0,
        },
        {
          metric: "Gross margin",
          shortLabel: "Margin",
          current: report.comparison.current?.gross_margin || 0,
          prior: report.comparison.prior?.gross_margin || 0,
        },
        {
          metric: "Expenses",
          shortLabel: "Expenses",
          current: report.comparison.current?.expenses || 0,
          prior: report.comparison.prior?.expenses || 0,
        },
        {
          metric: "Net operating",
          shortLabel: "Net result",
          current: report.comparison.current?.net_operating || 0,
          prior: report.comparison.prior?.net_operating || 0,
        },
      ]
    : [];

  const healthScoreEntries = report?.health_scores
    ? Object.entries(report.health_scores)
    : [];

  const executive = report?.executive_summary || {};
  const overallScore = executive.overall_health_score || 0;
  const deep = report?.deep_analysis || {};
  const creditAging = deep.credit_aging || {};
  const productProfit = deep.product_profitability || {};
  const salesChannels = deep.sales_channels || {};
  const salesChannelChartData = (salesChannels.channels || []).map((row) => ({
    ...row,
    shortLabel:
      row.channel.length > 14 ? `${row.channel.slice(0, 12)}…` : row.channel,
  }));
  const nozzleUtil = deep.nozzle_utilization || {};
  const employeeMatrix = deep.employee_matrix || {};
  const irregularities = deep.irregularities || {};
  const expenseWaste = deep.expense_waste || {};
  const stockOpportunity = deep.stock_opportunity || {};
  const workingCapital = deep.working_capital || {};
  const profitPlaybook = deep.profit_playbook || [];
  const positiveHighlights = deep.positive_highlights || [];
  const actionPlaybook = profitPlaybook.filter((item) => item.priority !== "positive");
  const strengthPlaybook = profitPlaybook.filter((item) => item.priority === "positive");
  const positiveInsights = (report?.insights || []).filter((i) => i.severity === "positive");
  const improvementInsights = (report?.insights || []).filter((i) => i.severity !== "positive");

  const reportBody = (
    <>
      {loading ? (
        <div className="form-wrap mt-3 text-center">
          <Loader />
        </div>
      ) : !access.allowed ? (
        <div className="form-wrap mt-3">
          <div className="text-center">{access.message}</div>
        </div>
      ) : (
        <div className={`row ${embedded ? 'mt-2' : 'mt-md-5 mt-3'} form-container justify-content-center owner-analytics-report`}>
                {embedded && reportLoading && (
                <div className="col-md-12 mb-3 text-center py-4">
                  <Loader />
                  <p className="small text-muted mt-2 mb-0">Building analytics report…</p>
                </div>
                )}
                {!hideDateFilter && (
                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-date-filter">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-4 col-lg-3">
                        <label className="form-label small text-muted mb-1" htmlFor="analytics-from-date">
                          From date
                        </label>
                        <input
                          id="analytics-from-date"
                          type="date"
                          className="form-control"
                          min={minDate || undefined}
                          max={draftToDate || undefined}
                          value={draftFromDate}
                          onChange={(e) => setDraftFromDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 col-lg-3">
                        <label className="form-label small text-muted mb-1" htmlFor="analytics-to-date">
                          To date
                        </label>
                        <input
                          id="analytics-to-date"
                          type="date"
                          className="form-control"
                          min={draftFromDate || minDate || undefined}
                          value={draftToDate}
                          onChange={(e) => setDraftToDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 col-lg-3">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={handleApplyDateRange}
                          disabled={reportLoading}
                        >
                          {reportLoading ? "Analysing…" : "Run analysis"}
                        </button>
                      </div>
                      <div className="col-md-12 col-lg-3">
                        <p className="small text-muted mb-0 owner-date-filter__hint">
                          {appliedDateRange.from_date && appliedDateRange.to_date
                            ? `Comparing ${appliedDateRange.from_date} → ${appliedDateRange.to_date} vs prior period`
                            : "Select a date range for deep business analysis."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {reportLoading && !report ? (
                  <div className="col-md-12 text-center py-5">
                    <Loader />
                  </div>
                ) : report ? (
                  <>
                    <div className="col-md-12 mb-3">
                      <div className="dash-card owner-chart-card owner-analytics-executive">
                        <div className="row align-items-center">
                          <div className="col-lg-3 text-center mb-3 mb-lg-0">
                            <div
                              className="owner-analytics-score-ring"
                              style={{ "--score-color": healthScoreColor(overallScore) }}
                            >
                              <span className="owner-analytics-score-ring__value">{overallScore}</span>
                              <span className="owner-analytics-score-ring__label">
                                {executive.health_label || "Health"}
                              </span>
                            </div>
                            <p className="small text-muted mt-2 mb-0">
                              Overall business health ({executive.period_days || 0} days)
                            </p>
                          </div>
                          <div className="col-lg-9">
                            <h5 className="mb-2">Executive verdict</h5>
                            <p className="owner-analytics-verdict mb-3">{executive.verdict}</p>
                            <div className="row g-2">
                              {healthScoreEntries.map(([key, item]) => (
                                <div className="col-6 col-md-4 col-lg" key={key}>
                                  <div className="owner-analytics-pillar">
                                    <span className="owner-analytics-pillar__label">
                                      {key.replace(/_/g, " ")}
                                    </span>
                                    <span
                                      className="owner-analytics-pillar__score"
                                      style={{ color: healthScoreColor(item.score) }}
                                    >
                                      {item.score}
                                    </span>
                                    <span className="owner-analytics-pillar__tag">{item.label}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {positiveHighlights.length > 0 ? (
                      <div className="col-md-12 mb-3">
                        <div className="dash-card owner-chart-card owner-analytics-wins">
                          <h5 className="mb-1">What&apos;s working well</h5>
                          <p className="owner-chart-panel__subtitle mb-3">
                            Strengths to protect while you improve weaker areas
                          </p>
                          <div className="row g-3">
                            {positiveHighlights.map((item, index) => (
                              <div className="col-md-6 col-lg-4" key={`${item.title}-${index}`}>
                                <div className="owner-analytics-win-card">
                                  <span className="owner-analytics-win-card__category">{item.category}</span>
                                  <h6 className="owner-analytics-win-card__title">{item.title}</h6>
                                  <p className="owner-analytics-win-card__detail mb-0">{item.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="col-lg-5 mb-3">
                      <div className="dash-card owner-chart-card owner-chart-panel owner-analytics-chart-card h-100">
                        <h5 className="mb-1">Health radar</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Five dimensions of pump performance (0–100)
                        </p>
                        {report.radar?.length > 0 ? (
                          <ChartWrap height={260}>
                            <RadarChart data={report.radar} cx="50%" cy="50%" outerRadius="68%" margin={{ top: 16, right: 24, bottom: 16, left: 24 }}>
                              <PolarGrid stroke="#e8e4f8" />
                              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#64748B" }} />
                              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} axisLine={false} />
                              <Radar
                                name="Score"
                                dataKey="score"
                                stroke="#5D38DB"
                                fill="#5D38DB"
                                fillOpacity={0.3}
                                strokeWidth={2}
                              />
                              <Tooltip formatter={(value) => [`${value}/100`, "Score"]} />
                            </RadarChart>
                          </ChartWrap>
                        ) : (
                          <div className="owner-chart-empty">No radar data for this period.</div>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-7 mb-3">
                      <div className="dash-card owner-chart-card owner-chart-panel owner-analytics-chart-card h-100">
                        <h5 className="mb-1">Period comparison</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Current vs prior {executive.period_days || 0}-day window
                        </p>
                        {comparisonChartData.length > 0 ? (
                          <ChartWrap height={260}>
                            <BarChart
                              data={comparisonChartData}
                              layout="vertical"
                              margin={{ top: 8, right: 16, left: 4, bottom: 8 }}
                              barCategoryGap="20%"
                              barGap={4}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0eef8" horizontal={false} />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 10 }}
                                tickFormatter={formatAxisCurrency}
                                domain={["auto", "auto"]}
                              />
                              <YAxis
                                type="category"
                                dataKey="shortLabel"
                                width={72}
                                tick={{ fontSize: 11, fill: "#475569" }}
                              />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                              <Bar dataKey="current" name="Current" fill="#5D38DB" radius={[0, 4, 4, 0]} maxBarSize={18} />
                              <Bar dataKey="prior" name="Prior period" fill="#94A3B8" radius={[0, 4, 4, 0]} maxBarSize={18} />
                            </BarChart>
                          </ChartWrap>
                        ) : (
                          <div className="owner-chart-empty">No comparison data.</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12 mb-3">
                      <div className="dash-card owner-chart-card">
                        <h5 className="mb-3">Scorecard</h5>
                        <div className="table-responsive">
                          <table className="table table-sm owner-analytics-table mb-0">
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th className="text-end">Current</th>
                                <th className="text-end">Prior</th>
                                <th className="text-end">Change</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(report.scorecard || []).map((row) => (
                                <tr key={row.key}>
                                  <td>{row.label}</td>
                                  <td className="text-end">{formatScorecardValue(row)}</td>
                                  <td className="text-end text-muted">
                                    {row.format === "percent"
                                      ? formatPercent(row.prior_value)
                                      : formatCurrency(row.prior_value)}
                                  </td>
                                  <td
                                    className={`text-end owner-analytics-delta owner-analytics-delta--${row.direction}`}
                                  >
                                    {row.format === "percent"
                                      ? `${row.delta >= 0 ? "+" : ""}${formatNumber(row.delta, 1)} pp`
                                      : `${row.delta >= 0 ? "+" : ""}${formatCurrency(row.delta)}`}
                                    <span className="small text-muted ms-1">
                                      ({row.delta_pct >= 0 ? "+" : ""}
                                      {formatNumber(row.delta_pct, 1)}%)
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <OwnerWorkingCapitalPanel workingCapital={workingCapital} />

                    {actionPlaybook.length > 0 ? (
                      <div className="col-md-12 mb-3">
                        <div className="dash-card owner-chart-card owner-analytics-playbook">
                          <h5 className="mb-1">Action plan</h5>
                          <p className="owner-chart-panel__subtitle mb-3">
                            Prioritised steps to improve profit and operations — ordered by urgency
                          </p>
                          <div className="owner-analytics-playbook-list">
                            {actionPlaybook.map((item, index) => (
                              <div
                                className={`owner-analytics-playbook-item owner-analytics-playbook-item--${item.priority}`}
                                key={`${item.title}-${index}`}
                              >
                                <div className="owner-analytics-playbook-item__head">
                                  <span className="owner-analytics-playbook-item__priority">{item.priority}</span>
                                  <strong>{item.title}</strong>
                                </div>
                                <p className="mb-1">{item.action}</p>
                                <p className="owner-analytics-playbook-item__why mb-0">{item.why}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {strengthPlaybook.length > 0 ? (
                      <div className="col-md-12 mb-3">
                        <div className="dash-card owner-chart-card owner-analytics-strengths">
                          <h5 className="mb-1">Protect your strengths</h5>
                          <p className="owner-chart-panel__subtitle mb-3">
                            Keep these practices — they are already helping your bottom line
                          </p>
                          <div className="row g-2">
                            {strengthPlaybook.map((item, index) => (
                              <div className="col-md-6" key={`strength-${index}`}>
                                <div className="owner-analytics-strength-item">
                                  <strong>{item.title}</strong>
                                  <p className="mb-0 small">{item.action}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-1">Credit aging</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Outstanding ₹{formatNumber(creditAging.total_outstanding || 0)} — stale:{" "}
                          {formatPercent(creditAging.stale_receivable_pct || 0)}
                        </p>
                        {(creditAging.aging_buckets || []).length > 0 ? (
                          <ChartWrap height={220}>
                            <BarChart data={creditAging.aging_buckets} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0eef8" horizontal={false} />
                              <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="bucket" width={76} tick={{ fontSize: 10 }} />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Bar dataKey="amount" fill="#DC2626" radius={[0, 4, 4, 0]} maxBarSize={22} />
                            </BarChart>
                          </ChartWrap>
                        ) : (
                          <div className="owner-chart-empty owner-chart-empty--compact">No credit outstanding.</div>
                        )}
                        {(creditAging.overdue_customers || []).length > 0 ? (
                          <div className="table-responsive mt-2">
                            <table className="table table-sm owner-analytics-table mb-0">
                              <thead>
                                <tr>
                                  <th>Overdue customer</th>
                                  <th className="text-end">Outstanding</th>
                                  <th className="text-end">Oldest (days)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {creditAging.overdue_customers.slice(0, 8).map((row) => {
                                  const ageVisual = getCreditAgeVisual(row.oldest_unpaid_days);

                                  return (
                                    <tr
                                      key={row.customer_id}
                                      className={`owner-wc-age-row ${ageVisual.rowClass}`}
                                      style={{ backgroundColor: ageVisual.rowBg }}
                                    >
                                      <td>{row.name}</td>
                                      <td className="text-end">{formatCurrency(row.outstanding)}</td>
                                      <td className="text-end">
                                        <span className={`owner-wc-status ${ageVisual.badgeClass}`}>
                                          {row.oldest_unpaid_days}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-1">Sales channels</h5>
                        <p className="owner-chart-panel__subtitle mb-3">Full revenue mix across payment modes</p>
                        {salesChannelChartData.length > 0 ? (
                          <ChartWrap height={220}>
                            <BarChart
                              data={salesChannelChartData}
                              layout="vertical"
                              margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0eef8" horizontal={false} />
                              <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="shortLabel" width={100} tick={{ fontSize: 10 }} />
                              <Tooltip
                                formatter={(value) => formatCurrency(value)}
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.channel || ""}
                              />
                              <Bar dataKey="amount" fill="#5D38DB" radius={[0, 4, 4, 0]} maxBarSize={22} />
                            </BarChart>
                          </ChartWrap>
                        ) : (
                          <div className="owner-chart-empty owner-chart-empty--compact">No channel data.</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12 mb-3">
                      <div className="dash-card owner-chart-card">
                        <h5 className="mb-1">Product profitability</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Blended margin {formatPercent(productProfit.summary?.blended_margin_pct || 0)} on ₹
                          {formatNumber((productProfit.summary?.total_sales || 0) / 100000, 1)}L sales
                        </p>
                        <div className="table-responsive">
                          <table className="table table-sm owner-analytics-table mb-0">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Category</th>
                                <th className="text-end">Sales</th>
                                <th className="text-end">Margin</th>
                                <th className="text-end">Margin %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(productProfit.top_margin || []).map((row) => (
                                <tr key={`${row.product}-${row.category}`}>
                                  <td><strong>{row.product}</strong></td>
                                  <td>{row.category}</td>
                                  <td className="text-end">{formatCurrency(row.sales)}</td>
                                  <td className="text-end">{formatCurrency(row.margin)}</td>
                                  <td className={`text-end ${row.margin_pct < 3 ? "text-danger" : ""}`}>
                                    {formatPercent(row.margin_pct)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mb-3">
                      <div className="dash-card owner-chart-card">
                        <h5 className="mb-1">Nozzle utilization</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Fair share {formatPercent(nozzleUtil.summary?.fair_share_pct || 0)} per nozzle — litres/hr
                          proxy for queue pressure (waiting time not recorded in system)
                        </p>
                        <div className="table-responsive">
                          <table className="table table-sm owner-analytics-table mb-0">
                            <thead>
                              <tr>
                                <th>Nozzle</th>
                                <th>Fuel</th>
                                <th className="text-end">Share %</th>
                                <th className="text-end">L/hr</th>
                                <th>Status</th>
                                <th>Recommendation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(nozzleUtil.nozzles || []).map((row) => (
                                <tr key={row.nozzle_id}>
                                  <td><strong>{row.name}</strong></td>
                                  <td>{row.fuel}</td>
                                  <td className="text-end">{formatPercent(row.share_pct)}</td>
                                  <td className="text-end">{formatNumber(row.litres_per_hour, 1)}</td>
                                  <td>
                                    <span className={`owner-analytics-nozzle-status owner-analytics-nozzle-status--${row.status}`}>
                                      {row.status}
                                    </span>
                                  </td>
                                  <td className="small text-muted">{row.recommendation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Employee performance matrix</h5>
                        {["star", "solid", "coaching", "risk"].map((tier) => {
                          const rows = employeeMatrix.tiers?.[tier] || [];
                          if (rows.length === 0) {
                            return null;
                          }
                          return (
                            <div className="owner-analytics-tier mb-3" key={tier}>
                              <h6 className={`owner-analytics-tier__label owner-analytics-tier__label--${tier}`}>
                                {tier} ({rows.length})
                              </h6>
                              {rows.slice(0, 5).map((emp) => (
                                <div className="owner-analytics-tier__row" key={emp.emp_id}>
                                  <span>{emp.name}</span>
                                  <span className="small text-muted">
                                    ₹{formatNumber(emp.sales_per_hour, 0)} fuel sales/hr · {emp.verdict}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Employee × nozzle pairs</h5>
                        <div className="table-responsive">
                          <table className="table table-sm owner-analytics-table mb-0">
                            <thead>
                              <tr>
                                <th>Employee</th>
                                <th>Nozzle</th>
                                <th className="text-end">Sales</th>
                                <th className="text-end">L/hr</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(employeeMatrix.emp_nozzle_pairs || []).slice(0, 10).map((row, i) => (
                                <tr key={`${row.employee}-${row.nozzle}-${i}`}>
                                  <td>{row.employee}</td>
                                  <td>{row.nozzle}</td>
                                  <td className="text-end">{formatCurrency(row.sales)}</td>
                                  <td className="text-end">{formatNumber(row.litres_per_hour, 1)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Irregularities &amp; leaks</h5>
                        {(irregularities.signals || []).length > 0 ? (
                          <div className="owner-analytics-rec-list">
                            {irregularities.signals.map((sig, i) => (
                              <div className={`owner-analytics-rec ${priorityClass(sig.severity === "critical" ? "high" : "medium")}`} key={i}>
                                <div className="owner-analytics-rec__head">
                                  <span className="owner-analytics-rec__priority">{sig.severity}</span>
                                  <span className="owner-analytics-rec__area">{sig.type}</span>
                                </div>
                                <p className="owner-analytics-rec__action mb-0">{sig.detail}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="owner-chart-empty owner-chart-empty--compact">No major irregularities flagged.</div>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Expense waste &amp; stock opportunities</h5>
                        <p className="small text-muted">
                          Expenses are {formatPercent(expenseWaste.expense_to_margin_pct || 0)} of gross margin
                        </p>
                        {(expenseWaste.top_categories || []).slice(0, 3).map((cat) => (
                          <div className="owner-analytics-expense-row mb-2" key={cat.category}>
                            <strong>{cat.category}</strong> — {formatCurrency(cat.amount)}
                            <div className="small text-muted">{cat.verdict}</div>
                          </div>
                        ))}
                        <hr />
                        {(stockOpportunity.opportunities || []).slice(0, 5).map((opp, i) => (
                          <div className="owner-analytics-stock-opp mb-2" key={`${opp.product}-${i}`}>
                            <span className={`owner-analytics-focus-badge ${opp.priority === "high" ? "owner-analytics-focus--star" : "owner-analytics-focus--maintain"}`}>
                              {opp.type}
                            </span>{" "}
                            <strong>{opp.product}</strong>
                            <div className="small text-muted">{opp.detail}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {(deep.data_notes || []).length > 0 ? (
                      <div className="col-md-12 mb-3">
                        <p className="small text-muted mb-0">
                          <strong>Data notes:</strong> {deep.data_notes.join(" ")}
                        </p>
                      </div>
                    ) : null}

                    {positiveInsights.length > 0 ? (
                      <div className="col-md-12 mb-3">
                        <div className="dash-card owner-chart-card">
                          <h5 className="mb-1">Positive signals</h5>
                          <p className="owner-chart-panel__subtitle mb-3">
                            Wins and healthy trends from this period
                          </p>
                          <div className="row g-3">
                            {positiveInsights.map((insight, index) => (
                              <div className="col-md-6" key={`pos-${insight.title}-${index}`}>
                                <div className={`owner-analytics-insight ${severityClass("positive")}`}>
                                  <div className="owner-analytics-insight__head">
                                    <span className="owner-analytics-insight__badge">strength</span>
                                    <span className="owner-analytics-insight__category">{insight.category}</span>
                                  </div>
                                  <h6 className="owner-analytics-insight__title">{insight.title}</h6>
                                  <p className="owner-analytics-insight__detail mb-0">{insight.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="col-md-12 mb-3">
                      <div className="dash-card owner-chart-card">
                        <h5 className="mb-1">Areas to improve</h5>
                        <p className="owner-chart-panel__subtitle mb-3">
                          Balanced view — issues and opportunities ranked by impact
                        </p>
                        <div className="row g-3">
                          {improvementInsights.length > 0 ? (
                            improvementInsights.map((insight, index) => (
                              <div className="col-md-6" key={`${insight.title}-${index}`}>
                                <div className={`owner-analytics-insight ${severityClass(insight.severity)}`}>
                                  <div className="owner-analytics-insight__head">
                                    <span className="owner-analytics-insight__badge">{insight.severity}</span>
                                    <span className="owner-analytics-insight__category">{insight.category}</span>
                                  </div>
                                  <h6 className="owner-analytics-insight__title">{insight.title}</h6>
                                  <p className="owner-analytics-insight__detail mb-0">{insight.detail}</p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="col-12">
                              <div className="owner-analytics-insight owner-analytics-insight--positive">
                                <h6 className="owner-analytics-insight__title">No major issues flagged</h6>
                                <p className="owner-analytics-insight__detail mb-0">
                                  Core metrics look stable this period. Keep monitoring weekly and protect the strengths above.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Recommendations</h5>
                        <div className="owner-analytics-rec-list">
                          {(report.recommendations || []).map((rec, index) => (
                            <div className={`owner-analytics-rec ${priorityClass(rec.priority)}`} key={index}>
                              <div className="owner-analytics-rec__head">
                                <span className="owner-analytics-rec__priority">{rec.priority} priority</span>
                                <span className="owner-analytics-rec__area">{rec.area}</span>
                              </div>
                              <p className="owner-analytics-rec__action mb-1">{rec.action}</p>
                              <p className="owner-analytics-rec__impact mb-0">
                                <strong>Expected impact:</strong> {rec.expected_impact}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                      <div className="dash-card owner-chart-card h-100">
                        <h5 className="mb-3">Product focus</h5>
                        <div className="table-responsive">
                          <table className="table table-sm owner-analytics-table mb-0">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Focus</th>
                                <th className="text-end">Mix %</th>
                                <th className="text-end">Stock (L)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(report.product_focus || []).map((row) => (
                                <tr key={row.product}>
                                  <td>
                                    <strong>{row.product}</strong>
                                    <div className="small text-muted">{row.reason}</div>
                                  </td>
                                  <td>
                                    <span className={`owner-analytics-focus-badge ${focusClass(row.focus)}`}>
                                      {row.focus}
                                    </span>
                                  </td>
                                  <td className="text-end">{formatPercent(row.sales_share_pct)}</td>
                                  <td className="text-end">{formatNumber(row.litres, 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="col-md-12">
                    <div className="owner-chart-empty">Unable to load analytics. Try another date range.</div>
                  </div>
                )}
              </div>
            )}
    </>
  );

  if (embedded) {
    return <div className="admin-embedded-owner-view">{reportBody}</div>;
  }

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={33} operation={4} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Data Analytics Report" />
          <div className="container pb-3">{reportBody}</div>
        </div>
      </div>
    </div>
  );
};

export default OwnerAnalyticsReport;
